import { PrismicProvider } from "@prismicio/react";
import { ToastProvider } from "@radix-ui/react-toast";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { useI18N } from "@sundae/react-hooks";
import { ExternalLink, Image, Text } from "@sundae/ui-toolkit";
import type { IWalletProviderOptions } from "@sundae/wallet-react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import request from "graphql-request";
import { FC, ReactNode, lazy, useMemo } from "react";

import HotjarProvider from "./components/Hotjar";
import { APP_NAME } from "./constants/page.constants";
import { GetAssetsByIdsDocument } from "./gql/generated/stats2.sdk";
import { prismicClient } from "./prismic/prismic.config";
import { AppContextProvider, ProfileAppContextProvider } from "./stores";
import { FirstVisitContextProvider } from "./stores/FirstVisitContext";
import { OrderContextProvider } from "./stores/OrderContext";
import { useWalletStrategyContext } from "./stores/WalletStrategyContext";
import { IAssetMetaData } from "./types/Asset.types";
import { getAssetWarning } from "./utils/assets.utils";
import { prismicLinkResolver } from "./utils/prismic.utils";

const WalletProvider = lazy(() =>
  import("@sundae/wallet-react").then((m) => ({ default: m.WalletProvider }))
);

export type TProvidersProps = {
  children: ReactNode;
};

const queryClient = new QueryClient();

export const Providers: FC<TProvidersProps> = ({ children }) => {
  const { t } = useI18N();
  const { strategy } = useWalletStrategyContext();

  const options = useMemo<IWalletProviderOptions>(
    () => ({
      debug: window.__APP_CONFIG.envName === "local",
      useHandles: true,
      strategy,
      dAppName: APP_NAME,
      metadataResolver: async function (assetIds): Promise<IAssetMetaData[]> {
        const { assets } = await request(
          window.__APP_CONFIG.apiUrls.stats2,
          GetAssetsByIdsDocument,
          {
            ids: assetIds,
          }
        );

        return assets.map((asset: IAssetMetaData) => {
          return {
            ...asset,
            warning: getAssetWarning(asset),
          };
        });
      },
    }),
    [strategy]
  );

  return (
    <WalletProvider options={options}>
      <ProfileAppContextProvider>
        <AppContextProvider>
          <HotjarProvider>
            <QueryClientProvider client={queryClient}>
              <OrderContextProvider>
                <TooltipProvider delayDuration={150}>
                  <ToastProvider label={t("news")}>
                    <PrismicProvider
                      client={prismicClient}
                      richTextComponents={{
                        heading1: ({ children }) => (
                          <Text tag="h1" size="xl">
                            {children}
                          </Text>
                        ),

                        heading2: ({ children }) => (
                          <Text tag="h2" size="lg">
                            {children}
                          </Text>
                        ),
                        heading3: ({ children }) => <Text tag="h3">{children}</Text>,
                        heading4: ({ children }) => <Text tag="h4">{children}</Text>,
                        paragraph: ({ children }) => (
                          <Text tag="p" size="sm">
                            {children}
                          </Text>
                        ),
                        hyperlink: ({ node, text }) => {
                          return (
                            <ExternalLink
                              arrow="top-right"
                              size="sm"
                              variant="primary"
                              href={prismicLinkResolver(node.data)}
                            >
                              {text}
                            </ExternalLink>
                          );
                        },
                        image: ({ node }) => (
                          <Image
                            className="my-2"
                            src={node.url}
                            alt={node.alt ?? "Sundae Labs"}
                            width={node.dimensions.width}
                            height={node.dimensions.height}
                          />
                        ),
                      }}
                    >
                      {children}
                    </PrismicProvider>
                  </ToastProvider>
                </TooltipProvider>
                <ReactQueryDevtools position="bottom-right" />
              </OrderContextProvider>
            </QueryClientProvider>
          </HotjarProvider>
        </AppContextProvider>
      </ProfileAppContextProvider>
    </WalletProvider>
  );
};

/**
 * These providers are used here to render information/components after the initial page load animation.
 */
export const ProvidersAfterInitialRender: FC<TProvidersProps> = ({ children }) => {
  return <FirstVisitContextProvider>{children}</FirstVisitContextProvider>;
};

export default Providers;
